import React from 'react'
import Money from 'uiComponents/money'
import { IOrder, IOrderItem, OrderTicketStatus } from 'orders/schema'
import RedeemedOn from 'orders/orders/ordersTable/orderDetails/redeemedOn'
import { Link } from 'react-router-dom'
import OrderItemStatus from 'orders/orders/ordersTable/orderDetails/orderItemStatus'
import ItemSection from './components/itemSection'
import SectionDataGrid from './components/sectionDataGrid'
import './styles.scss'
import ValidTo from 'orders/orders/commons/validTo'
import { convertTimeFormat, DateFormats, formatISOString } from 'utils/dates'
import { useAppSelector } from 'store/hooks'
import { getSelectedOrder } from 'orders/orders/redux'
import { CopyIcon } from 'uiComponents/buttons/copyIcon'

interface ItemDetailsProps {
    item: IOrderItem
    order?: IOrder
    accountSlug: string
    isItemSelected: boolean
    isItemCanceledRefunded: boolean
    shouldShowPricesWithoutTax: boolean
    shouldShowBookingFees: boolean
}

const ItemDetailsSection = ({
    item,
    order,
    accountSlug,
    isItemSelected,
    isItemCanceledRefunded,
    shouldShowPricesWithoutTax,
    shouldShowBookingFees,
}: ItemDetailsProps) => {
    const selectedOrder = order ?? useAppSelector(getSelectedOrder)
    const detailsData = React.useMemo(
        () => [
            {
                name: 'Barcode',
                value: (
                    <>
                        {item.barcode?.barcode}
                        <CopyIcon text={item.barcode?.barcode} />
                    </>
                ),
            },
            {
                name: 'Product ID',
                value: (
                    <Link
                        title={item.productNumericId.toString()}
                        to={`/account/${accountSlug}/products/crud/${item.productId}`}
                        target="_blank"
                    >
                        {item.productNumericId}
                    </Link>
                ),
            },
            { name: 'Redeemed on', value: <RedeemedOn value={item.barcode?.redeemed ?? undefined} /> },
            {
                name: 'Visit date',
                value: item.visitDate ? formatISOString(item.visitDate, DateFormats.LONG_DATE) : '-',
            },
            {
                name: 'Fees (excl. tax)',
                value: <Money amount={item.bookingFeeExclTax} accountSlug={accountSlug} />,
                hide: !shouldShowBookingFees || !shouldShowPricesWithoutTax,
            },
            {
                name: 'Fees',
                value: <Money amount={item.bookingFeeInclTax} accountSlug={accountSlug} />,
                hide: !shouldShowBookingFees,
            },
            {
                name: 'Valid from',
                value: item.validFrom ? formatISOString(item.validFrom, DateFormats.LONG_DATE) : '-',
            },
            {
                name: 'Valid to',
                value: item.validTo ? (
                    <ValidTo status={item.barcode?.status ?? ''} orderItem={item} order={selectedOrder} />
                ) : (
                    '-'
                ),
            },
            {
                name: 'Visit time',
                value: item.visitTime ? convertTimeFormat(item.visitTime, 'HH:mm:ss', DateFormats.SHORT_TIME) : '-',
            },
            {
                name: 'Status',
                value: <OrderItemStatus value={item.status as OrderTicketStatus} />,
            },
        ],
        [item, accountSlug],
    )

    return (
        <ItemSection
            sectionName="Details"
            isItemSelected={isItemSelected}
            isItemCanceledRefunded={isItemCanceledRefunded}
        >
            <SectionDataGrid dataItems={detailsData} />
        </ItemSection>
    )
}

export default ItemDetailsSection
